'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import IdeaCard from '@/components/IdeaCard';
import Icon from '@/components/ui/AppIcon';
import { getPublishedIdeas, searchIdeas, CATEGORIES } from '@/lib/content';

const DIFFICULTY_FILTERS = [
  { label: 'All levels', value: '' },
  { label: 'Foundational', value: 'foundational' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
];

const TYPE_FILTERS = [
  { label: 'All types', value: '' },
  { label: 'Concept', value: 'concept' },
  { label: 'Principle', value: 'principle' },
  { label: 'Mistake', value: 'mistake' },
  { label: 'Trade-off', value: 'trade-off' },
  { label: 'Story', value: 'story' },
];

const SORT_OPTIONS = [
  { label: 'Newest', value: 'newest' },
  { label: 'A to Z', value: 'az' },
];

export default function ExplorePage() {
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const allIdeas = getPublishedIdeas();

  const filtered = useMemo(() => {
    let result = query?.trim() ? searchIdeas(query) : allIdeas;
    if (categoryFilter) {
      result = result?.filter(i => i?.primary_category === categoryFilter || i?.secondary_categories?.includes(categoryFilter));
    }
    if (difficultyFilter) {
      result = result?.filter(i => i?.difficulty === difficultyFilter);
    }
    if (typeFilter) {
      result = result?.filter(i => i?.editorial_type === typeFilter);
    }
    if (sortBy === 'az') {
      result = [...result]?.sort((a, b) => a?.title?.localeCompare(b?.title));
    }
    return result;
  }, [query, categoryFilter, difficultyFilter, typeFilter, sortBy, allIdeas]);

  const hasFilters = query || categoryFilter || difficultyFilter || typeFilter;

  return (
    <>
      <main className="pt-32 sm:pt-36 pb-20">
        {/* Page header */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-10">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Library</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h1
              className="font-display font-bold text-primary"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.0', letterSpacing: '-0.025em' }}
            >
              Explore the library
            </h1>
            <p className="text-muted-foreground max-w-sm leading-relaxed text-sm">
              {allIdeas?.length} published ideas across {CATEGORIES?.length} categories. More added regularly.
            </p>
          </div>
        </div>

        {/* Search + filters */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-8">
          {/* Search */}
          <div className="relative mb-5">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
              <Icon name="MagnifyingGlassIcon" size={18} variant="outline" />
            </span>
            <input
              type="text"
              placeholder="Search ideas, concepts, categories, tags..."
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
              className="search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="XMarkIcon" size={16} variant="outline" />
              </button>
            )}
          </div>

          {/* Filter chips row */}
          <div className="flex flex-wrap gap-2 mb-4">
            {/* Category filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategoryFilter('')}
                className={`filter-chip ${categoryFilter === '' ? 'active' : ''}`}
              >
                All categories
              </button>
              {CATEGORIES?.slice(0, 6)?.map((cat) => (
                <button
                  key={cat?.id}
                  onClick={() => setCategoryFilter(categoryFilter === cat?.id ? '' : cat?.id)}
                  className={`filter-chip ${categoryFilter === cat?.id ? 'active' : ''}`}
                >
                  {cat?.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {DIFFICULTY_FILTERS?.map((f) => (
              <button
                key={f?.value}
                onClick={() => setDifficultyFilter(f?.value)}
                className={`filter-chip ${difficultyFilter === f?.value ? 'active' : ''}`}
              >
                {f?.label}
              </button>
            ))}
            <span className="w-px h-6 bg-border self-center mx-1" />
            {TYPE_FILTERS?.map((f) => (
              <button
                key={f?.value}
                onClick={() => setTypeFilter(f?.value)}
                className={`filter-chip ${typeFilter === f?.value ? 'active' : ''}`}
              >
                {f?.label}
              </button>
            ))}
          </div>

          {/* Sort + view toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {SORT_OPTIONS?.map((opt) => (
                <button
                  key={opt?.value}
                  onClick={() => setSortBy(opt?.value)}
                  className={`filter-chip ${sortBy === opt?.value ? 'active' : ''}`}
                >
                  {opt?.label}
                </button>
              ))}
              {hasFilters && (
                <button
                  onClick={() => { setQuery(''); setCategoryFilter(''); setDifficultyFilter(''); setTypeFilter(''); }}
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-danger transition-colors ml-2"
                >
                  <Icon name="XMarkIcon" size={13} variant="outline" />
                  Clear filters
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 p-1 rounded-lg border border-border bg-card">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                aria-label="Grid view"
              >
                <Icon name="Squares2X2Icon" size={15} variant="outline" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                aria-label="List view"
              >
                <Icon name="ListBulletIcon" size={15} variant="outline" />
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {filtered?.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-5">
                <Icon name="MagnifyingGlassIcon" size={24} variant="outline" className="text-muted-foreground" />
              </div>
              <h3
                className="font-display font-semibold text-foreground text-xl mb-2"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                No sketches found
              </h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
                Try different search terms or browse by category. More ideas are published regularly.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['identity', 'cloud', 'MFA', 'zero trust', 'phishing']?.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setQuery(suggestion)}
                    className="filter-chip"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-6 font-medium">
                {filtered?.length} {filtered?.length === 1 ? 'idea' : 'ideas'} {hasFilters ? 'matching your filters' : 'published'}
              </p>

              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filtered?.map((idea) => (
                    <IdeaCard key={idea?.id} idea={idea} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col divide-y divide-border">
                  {filtered?.map((idea) => {
                    const cat = CATEGORIES?.find(c => c?.id === idea?.primary_category);
                    return (
                      <Link
                        key={idea?.id}
                        href={`/sketches/${idea?.slug}`}
                        className="group flex items-start gap-5 py-5 hover:bg-muted/30 rounded-xl px-3 -mx-3 transition-colors"
                      >
                        <div className="flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border border-border">
                          <img
                            src={idea?.illustration_url}
                            alt={idea?.illustration_alt}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {cat && <span className="category-pill">{cat?.name}</span>}
                            <span className="tag-chip capitalize">{idea?.difficulty}</span>
                          </div>
                          <h3
                            className="font-display font-semibold text-foreground text-base leading-snug group-hover:text-accent transition-colors duration-200 mb-1"
                            style={{ fontFamily: 'var(--font-display)' }}
                          >
                            {idea?.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-1">{idea?.reader_hook}</p>
                        </div>
                        <Icon name="ArrowRightIcon" size={14} variant="outline" className="flex-shrink-0 mt-1 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all duration-200" />
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}